import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../CustomCodes.redux';
import { Box, Button, IconButton, Popover, TextField } from '@mui/material';
import { Edit, ExpandMore, FormatListBulleted } from '@mui/icons-material';
import DragDropContainer from './DragDropContainer';
import customCodesSx from '../../CustomCodes.styles';
import Info from 'components/Info';
import { useTheme } from '@mui/material/styles';
import DashboardCard from 'components/DashboardCard';
import { set } from 'lodash';

const getDefaultConf = (category) => ({
    symbol_abbreviation: '',
    symbol_name: '',
    symbol_description: '',
    symbol_data_type: 'text',
    symbol_category: category,
    symbol_chart_config: {
        type: 'multiselect',
        options: [
            {
                codes: [],
                label: '',
            },
        ],
        attachment: 'mouth',
    },
});

const MultiSelect = () => {
    const theme = useTheme(),
        sx = customCodesSx(theme),
        dispatch = useDispatch(),
        category = useSelector((state) => state.customCodes.category),
        conf = useSelector((state) => state.customCodes.conf),
        expanded = useSelector((state) => state.customCodes.multiSelectExpanded),
        options = conf.symbol_chart_config?.options;

    const [sectionTitleEditing, setSectionTitleEditing] = React.useState([]);
    const [addSectionPopoverAnchorEl, setAddSectionPopoverAnchorEl] = React.useState(null);
    const [removeSectionPopover, setRemoveSectionPopover] = React.useState({ anchorEl: null, index: null });

    // Get the multi-select code list
    useEffect(() => {
        dispatch(actions.loadMultiSelectCodes());
    }, [dispatch]);

    // Set up the default configuration
    useEffect(() => {
        if (conf.symbol_name === undefined) {
            dispatch(actions.setState({ conf: getDefaultConf(category) }));
        }
    }, [dispatch, conf, category]);

    if (!Array.isArray(options)) {
        return null;
    }

    const handleChange = (index) => (e, isExpanded) => {
        dispatch(
            actions.setState({
                multiSelectExpanded: expanded === index ? null : index,
            })
        );
    };

    const setSectionLabel = (index) => (e) => {
        if (!sectionTitleEditing[index]) {
            setSectionTitleEditing((prev) => [...prev.slice(0, index), true, ...prev.slice(index + 1)]);
        }

        dispatch(actions.setSectionLabel(index, e.target.value));
    };

    const setCodeProperty = (property) => (e) => {
        dispatch(actions.setCodeProperty(property, e.target.value));
    };

    const openAddSectionPopover = (event) => {
        setAddSectionPopoverAnchorEl(event.currentTarget);
    };

    const closeAddSectionPopover = () => {
        setAddSectionPopoverAnchorEl(null);
    };

    const addSectionAt = (position) => {
        dispatch(actions.addSection(position));
        closeAddSectionPopover();
    };

    const openRemoveSectionPopover = (index) => (event) => {
        setRemoveSectionPopover({ anchorEl: event.currentTarget, index });
    };

    const closeRemoveSectionPopover = () => {
        setRemoveSectionPopover({ anchorEl: null, index: null });
    };

    const removeSection = (index) => {
        const nextOptions = options.filter((_, optionIndex) => optionIndex !== index);
        const nextExpanded = expanded === index ? null : expanded > index ? expanded - 1 : expanded;

        dispatch(
            actions.setState({
                conf: {
                    ...conf,
                    symbol_chart_config: {
                        ...conf.symbol_chart_config,
                        options: nextOptions,
                    },
                },
                multiSelectExpanded: nextExpanded,
            })
        );

        setSectionTitleEditing((prev) => prev.filter((_, editingIndex) => editingIndex !== index));
        closeRemoveSectionPopover();
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={sx.textInputWrapper}>
                <TextField
                    label="Custom Code Name"
                    onChange={setCodeProperty('symbol_name')}
                    value={conf.symbol_name}
                    sx={sx.textInput}
                    variant="outlined"
                    slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { maxLength: 80 },
                        input: {
                            endAdornment: (
                                <Info>
                                    <div>The full name of your code. This will appear on reports.</div>
                                </Info>
                            ),
                        },
                    }}
                />
            </Box>

            <Box sx={sx.textInputWrapper}>
                <TextField
                    label="Abbreviation"
                    onChange={setCodeProperty('symbol_abbreviation')}
                    value={conf.symbol_abbreviation}
                    sx={sx.textInput}
                    variant="outlined"
                    slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { maxLength: 8 },
                        input: {
                            endAdornment: (
                                <Info>
                                    <div>The abbreviation of your code. This will appear on charts.</div>
                                </Info>
                            ),
                        },
                    }}
                />
            </Box>

            <Box sx={sx.textInputWrapper}>
                <TextField
                    label="Description"
                    onChange={setCodeProperty('symbol_description')}
                    value={conf.symbol_description}
                    sx={sx.textInput}
                    variant="outlined"
                    slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { maxLength: 400 },
                        input: {
                            endAdornment: (
                                <Info>
                                    <div>
                                        A detailed description of your code. This will appear on the "All Codes" screen
                                        on the charting page.
                                    </div>
                                </Info>
                            ),
                        },
                    }}
                />
            </Box>

            <Box sx={sx.textInputWrapper}>
                <Button variant="contained" color="primary" onClick={openAddSectionPopover}>
                    Add Section
                </Button>
                <Popover
                    open={Boolean(addSectionPopoverAnchorEl)}
                    anchorEl={addSectionPopoverAnchorEl}
                    onClose={closeAddSectionPopover}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', minWidth: '180px', gap: 0.5 }}>
                        <Button
                            size="small"
                            onClick={() => addSectionAt('start')}
                            sx={{ justifyContent: 'flex-start' }}
                        >
                            Beginning of list
                        </Button>
                        <Button size="small" onClick={() => addSectionAt('end')} sx={{ justifyContent: 'flex-start' }}>
                            End of list
                        </Button>
                    </Box>
                </Popover>
            </Box>

            <Box sx={{ ...sx.textInputWrapper, maxWidth: '1200px' }}>
                {options.map((option, index) => {
                    const isEditingMode = sectionTitleEditing[index] === true || !option.label;

                    return (
                        <Box key={index} sx={{ mb: '64px' }}>
                            <DashboardCard
                                key={index}
                                darkMode={true}
                                color={'orange'}
                                icon={<FormatListBulleted />}
                                topLabel={
                                    options.length > 1 && (
                                        <>
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={openRemoveSectionPopover(index)}
                                            >
                                                Remove section
                                            </Button>
                                            <Popover
                                                open={removeSectionPopover.index === index}
                                                anchorEl={removeSectionPopover.anchorEl}
                                                onClose={closeRemoveSectionPopover}
                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                            >
                                                <Box sx={{ p: 2, maxWidth: '240px' }}>
                                                    <Box sx={{ mb: 1.5 }}>
                                                        Are you sure you want to remove this section?
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                        <Button size="small" onClick={closeRemoveSectionPopover}>
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            variant="contained"
                                                            onClick={() => removeSection(index)}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </Popover>
                                        </>
                                    )
                                }
                                topContent={
                                    <>
                                        {isEditingMode && (
                                            <TextField
                                                label={'Section Name'}
                                                value={option.label}
                                                onChange={setSectionLabel(index)}
                                                sx={{ clear: 'both', width: '300px' }}
                                                slotProps={{ htmlInput: { maxLength: 50 } }}
                                            />
                                        )}
                                        {!isEditingMode && (
                                            <>
                                                {option.label}{' '}
                                                <IconButton
                                                    onClick={() =>
                                                        setSectionTitleEditing((prev) => [
                                                            ...prev.slice(0, index),
                                                            true,
                                                            ...prev.slice(index + 1),
                                                        ])
                                                    }
                                                >
                                                    <Edit />
                                                </IconButton>
                                            </>
                                        )}
                                    </>
                                }
                                size={'xl'}
                            >
                                <Box sx={{ clear: 'both', mt: '12.5px', width: '100%' }}>
                                    <DragDropContainer index={index} />
                                </Box>
                            </DashboardCard>
                        </Box>
                    );
                })}
            </Box>

            <Button variant="contained" color="primary" onClick={() => dispatch(actions.handleMultiSelectSave())}>
                Save
            </Button>
        </Box>
    );
};

export default MultiSelect;
